import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import projectLocations from '../../data/projectLocations.json';
import MapHeader from './map/MapHeader';
import MapCanvas from './map/MapCanvas';
import MapLegend from './map/MapLegend';
import MapDetailCard from './map/MapDetailCard';

function computeStats(locations) {
  const states = new Set(locations.map((l) => l.state).filter(Boolean));
  let current = 0;
  let closed = 0;
  for (const loc of locations) {
    if (loc.status === 'current') current += 1;
    else closed += 1;
  }
  return { totalStates: states.size, currentCount: current, closedCount: closed };
}

export default function InteractiveMap() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.05 });
  const [selectedLocation, setSelectedLocation] = useState(null);

  const { totalStates, currentCount, closedCount } = useMemo(
    () => computeStats(projectLocations),
    [],
  );

  return (
    <section
      id="work"
      className="relative py-20 sm:py-24 lg:py-28 bg-background text-foreground overflow-hidden border-t border-border"
      data-testid="map-section"
    >
      <div className="absolute inset-0 bg-grain opacity-[0.04] pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" ref={ref}>
        <MapHeader totalStates={totalStates} inView={inView} />

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="relative rounded-xl overflow-hidden border border-border shadow-elev-2"
          style={{ height: '620px' }}
          data-testid="interactive-map"
        >
          <MapCanvas
            locations={projectLocations}
            selectedId={selectedLocation?.id}
            onMarkerClick={setSelectedLocation}
          />
          <MapLegend
            totalLocations={projectLocations.length}
            totalStates={totalStates}
          />
        </motion.div>

        <MapDetailCard
          location={selectedLocation}
          onClose={() => setSelectedLocation(null)}
        />

        <motion.div
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="mt-10 max-w-3xl"
        >
          <p className="text-foreground/60 text-sm leading-relaxed">
            Established 1999. OMC crews have worked on high-security and elevated-safety
            sites including military bases, nuclear power facilities, ammonia, and
            petroleum plants.
          </p>
        </motion.div>
      </div>
    </section>
  );
}
